/**
 * Application shell.
 *
 * Owns settings and mode selection, mounts exactly one mode component, and
 * holds the toasts. Everything that changes globally lives here so that
 * switching modes never tears down the live session — a user flipping from
 * Voice to Subtitles mid-conversation expects the translation to keep running.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { AppMode } from "@shared/constants";
import type { LanguageCode } from "@shared/types/language";
import type { PublicConfig } from "@shared/types/api";
import { languageLabel, languageShortLabel, toTtsCode } from "@shared/languages";
import { useTranslationSession } from "./lib/use-session";
import { loadSettings, resetSettings, saveSettings, type Settings } from "./lib/storage";
import { isBrowserAsrAvailable, isBrowserTtsAvailable } from "./lib/browser-voice";
import { isPiperSupported } from "./lib/piper";
import { ToastStack, type Toast, type ToastTone } from "./components/ui";
import { TextMode } from "./components/TextMode";
import { VoiceMode } from "./components/VoiceMode";
import { ConversationMode } from "./components/ConversationMode";
import { SubtitleMode } from "./components/SubtitleMode";
import { SettingsDialog } from "./components/SettingsDialog";
import { VoicesDialog } from "./components/VoicesDialog";
import { IconConversation, IconMic, IconMoon, IconSettings, IconSpeaker, IconSparkle, IconSubtitles, IconSun, IconText } from "./components/icons";

const MODES: readonly { value: AppMode; label: string; icon: React.ReactNode }[] = [
  { value: "text", label: "Text", icon: <IconText size={14} /> },
  { value: "voice", label: "Voice", icon: <IconMic size={14} /> },
  { value: "conversation", label: "Conversation", icon: <IconConversation size={14} /> },
  { value: "subtitles", label: "Subtitles", icon: <IconSubtitles size={14} /> },
];

export function App() {
  const [settings, setSettings] = useState<Settings>(() => loadSettings());
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [voicesOpen, setVoicesOpen] = useState(false);
  const [resolvedTheme, setResolvedTheme] = useState<"dark" | "light">("dark");
  const [config, setConfig] = useState<PublicConfig | null>(null);

  const pushToast = useCallback((title: string, tone: ToastTone, detail?: string) => {
    setToasts((current) => [...current.slice(-3), { id: `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, title, tone, detail }]);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  // -- settings --------------------------------------------------------------

  const patchSettings = useCallback((patch: Partial<Settings>) => {
    setSettings((current) => {
      const next = { ...current, ...patch };
      saveSettings(next);
      return next;
    });
  }, []);

  const onResetSettings = useCallback(() => {
    const next = resetSettings();
    setSettings(next);
    pushToast("Settings reset", "info");
  }, [pushToast]);

  // -- theme -----------------------------------------------------------------

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: light)");
    const apply = (): void => {
      const next = settings.theme === "system" ? (media.matches ? "light" : "dark") : settings.theme;
      setResolvedTheme(next);
      document.documentElement.dataset["theme"] = next;
    };
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [settings.theme]);

  // -- session ---------------------------------------------------------------

  const session = useTranslationSession({
    sourceLanguage: settings.sourceLanguage,
    targetLanguage: settings.targetLanguage,
    mode: settings.mode,
    audioOutput: settings.audioOutput,
    speakTranslations: settings.speakTranslations,
    persistTranscript: settings.persistTranscript,
    useBrowserAsr: settings.useBrowserAsr,
    useBrowserTts: settings.useBrowserTts,
    usePiperTts: settings.usePiperTts,
    piperGender: settings.piperGender,
    piperVoiceId: settings.piperVoiceId,
    inputDeviceId: settings.inputDeviceId,
    partialTranslation: settings.partialTranslation,
    secondaryLanguage: settings.targetLanguage,
    onToast: pushToast,
  });

  // Text mode needs a live `/api/config` for its capability checks, and the
  // session hook already fetches one; this just mirrors it into state.
  useEffect(() => {
    void session.refreshConfig().then((value) => {
      if (value) setConfig(value);
    });
    // Only on mount — the session hook owns the refresh lifecycle after that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -- language actions ------------------------------------------------------

  const onSwap = useCallback(() => {
    setSettings((current) => {
      if (current.sourceLanguage === "auto") return current;
      const next: Settings = { ...current, sourceLanguage: current.targetLanguage, targetLanguage: current.sourceLanguage };
      saveSettings(next);
      return next;
    });
  }, []);

  const setSource = useCallback(
    (value: LanguageCode | "auto") => {
      patchSettings({ sourceLanguage: value, autoDetect: value === "auto" });
    },
    [patchSettings],
  );

  const setTarget = useCallback(
    (value: LanguageCode) => {
      patchSettings({ targetLanguage: value });
    },
    [patchSettings],
  );

  // -- derived ---------------------------------------------------------------

  const canSpeak = useCallback(
    (language: LanguageCode): boolean => {
      // Piper covers 30+ languages on-device, the browser most of the rest for
      // free; the server covers whatever is left.
      if (settings.usePiperTts && isPiperSupported()) return true;
      if (settings.useBrowserTts && isBrowserTtsAvailable()) return true;
      return toTtsCode(language) !== null;
    },
    [settings.usePiperTts, settings.useBrowserTts],
  );

  const browser = useMemo(
    () => ({
      speechRecognition: isBrowserAsrAvailable(),
      speechSynthesis: isBrowserTtsAvailable(),
      audioWorklet: typeof AudioWorklet !== "undefined",
    }),
    [],
  );

  const effectiveSource: LanguageCode = settings.sourceLanguage === "auto" ? (session.detection?.code ?? "en") : settings.sourceLanguage;

  // -- render ----------------------------------------------------------------

  return (
    <div className="app">
      <header
        className="row-between topbar"
        style={{ padding: "var(--space-s) var(--space-m)", borderBottom: "1px solid var(--line)", background: "var(--bg-raised)", flex: "none" }}
      >
        <div className="row" style={{ gap: "var(--space-s)", minWidth: 0 }}>
          <Mark />
          <div className="stack" style={{ gap: 0, minWidth: 0 }}>
            <span style={{ fontWeight: 650, letterSpacing: "-0.02em", fontSize: "var(--step-0)" }}>Dream Tongue</span>
            <span className="dim" style={{ fontSize: "var(--step--2)" }}>
              {config === null ? "connecting…" : config.mockAi ? "development mode · simulated" : `${config.capabilities.languages.length} languages`}
            </span>
          </div>
        </div>

        <div className="row topbar__modes" style={{ gap: "var(--space-2xs)", minWidth: 0, flex: "1 1 auto", justifyContent: "center" }}>
          <div className="segmented" role="tablist" aria-label="Mode">
            {MODES.map((mode) => (
              <button
                key={mode.value}
                type="button"
                role="tab"
                className="segmented__item"
                data-tone="brand"
                aria-selected={settings.mode === mode.value}
                onClick={() => patchSettings({ mode: mode.value })}
              >
                {mode.icon}
                <span className="hide-sm">{mode.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="row" style={{ gap: "var(--space-2xs)" }}>
          {session.isLive ? <LiveBadge /> : null}
          <button
            type="button"
            className="btn btn--ghost btn--icon"
            // The old handler flipped local state without touching settings,
            // so the theme snapped back on every reload and disagreed with
            // the Settings dialog. The setting is the source of truth now.
            onClick={() => patchSettings({ theme: resolvedTheme === "dark" ? "light" : "dark" })}
            title={resolvedTheme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            aria-label={resolvedTheme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
          >
            {resolvedTheme === "dark" ? <IconSun size={17} /> : <IconMoon size={17} />}
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--icon"
            onClick={() => setVoicesOpen(true)}
            title="Voices"
            aria-label="Voices"
          >
            <IconSpeaker size={17} />
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--icon"
            onClick={() => setSettingsOpen(true)}
            title="Settings"
            aria-label="Settings"
          >
            <IconSettings size={17} />
          </button>
        </div>
      </header>

      {settings.mode === "text" ? (
        <TextMode
          sourceLanguage={settings.sourceLanguage}
          targetLanguage={settings.targetLanguage}
          onSourceChange={setSource}
          onTargetChange={setTarget}
          translate={session.translateText}
          detect={session.detectText}
          speak={session.speak}
          cancelSpeech={session.cancelSpeech}
          canSpeak={canSpeak}
          transcript={session.transcript}
          historyEnabled={settings.historyEnabled}
          onToast={pushToast}
        />
      ) : null}

      {settings.mode === "voice" ? (
        <VoiceMode
          session={session}
          sourceLanguage={settings.sourceLanguage}
          targetLanguage={settings.targetLanguage}
          onSourceChange={setSource}
          onTargetChange={setTarget}
          onSwap={onSwap}
          showOriginal={settings.showOriginal}
          showTranslation={settings.showTranslation}
          canSpeak={canSpeak}
          speakOut={settings.speakTranslations}
          onSpeakOutChange={(speakTranslations) => patchSettings({ speakTranslations })}
          piperGender={settings.piperGender}
          onPiperGenderChange={(piperGender) => patchSettings({ piperGender })}
          onToast={pushToast}
        />
      ) : null}

      {settings.mode === "conversation" ? (
        <ConversationMode
          session={session}
          languageA={settings.sourceLanguage === "auto" ? effectiveSource : settings.sourceLanguage}
          languageB={settings.targetLanguage}
          onLanguageAChange={setSource}
          onLanguageBChange={setTarget}
          onSwap={onSwap}
          canSpeak={canSpeak}
          speakOut={settings.speakTranslations}
          onSpeakOutChange={(speakTranslations) => patchSettings({ speakTranslations })}
          piperGender={settings.piperGender}
          onPiperGenderChange={(piperGender) => patchSettings({ piperGender })}
        />
      ) : null}

      {settings.mode === "subtitles" ? (
        <SubtitleMode
          session={session}
          sourceLanguage={settings.sourceLanguage}
          targetLanguage={settings.targetLanguage}
          onSourceChange={setSource}
          onTargetChange={setTarget}
          showOriginal={settings.showOriginal}
          showTranslation={settings.showTranslation}
          subtitleSize={settings.subtitleSize}
          subtitlePosition={settings.subtitlePosition}
          theme={resolvedTheme}
          onSizeChange={(subtitleSize) => patchSettings({ subtitleSize })}
          onPositionChange={(subtitlePosition) => patchSettings({ subtitlePosition })}
          onThemeChange={(theme) => patchSettings({ theme })}
          canSpeak={canSpeak}
          onSpeak={session.speak}
        />
      ) : null}

      <footer
        className="row-between"
        style={{ padding: "var(--space-2xs) var(--space-m)", borderTop: "1px solid var(--line)", background: "var(--bg-raised)", flex: "none" }}
      >
        <div className="row" style={{ gap: "var(--space-2xs)", fontSize: "var(--step--2)" }}>
          <span className="dim">
            {settings.sourceLanguage === "auto" ? "Auto-detect" : languageLabel(settings.sourceLanguage)}
          </span>
          <span className="dim" aria-hidden="true">→</span>
          <span className="dim">{languageShortLabel(settings.targetLanguage)}</span>
          {settings.speakTranslations ? <span className="badge">speaking</span> : null}
          {settings.persistTranscript ? <span className="badge badge--warn">saving transcript</span> : null}
        </div>
        <div className="row" style={{ gap: "var(--space-2xs)" }}>
          <span className={`status`} data-state={session.state}>
            <span className="status__dot" />
            {session.state}
          </span>
          <span className="dim" style={{ fontSize: "var(--step--2)" }}>
            {session.stateDetail ?? (config ? `· ${config.deploymentMode} plan` : "")}
          </span>
        </div>
      </footer>

      <VoicesDialog
        open={voicesOpen}
        onClose={() => setVoicesOpen(false)}
        session={session}
        gender={settings.piperGender}
        onGenderChange={(piperGender) => patchSettings({ piperGender })}
      />

      <SettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={settings}
        onChange={patchSettings}
        onReset={onResetSettings}
        session={session}
        config={config}
        browser={browser}
      />

      <ToastStack toasts={toasts} onDismiss={dismissToast} />
      <span className="sr-only" aria-live="polite">
        {session.state === "listening" ? "Translating" : ""}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Mark() {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "grid",
        placeItems: "center",
        width: 34,
        height: 34,
        borderRadius: 10,
        background: "var(--brand-grad)",
        boxShadow: "var(--shadow-brand)",
        flex: "none",
      }}
    >
      <IconSparkle size={18} color="#fff" />
    </span>
  );
}

function LiveBadge() {
  return (
    <span className="status" data-state="listening" title="A session is live">
      <span className="status__dot" />
      live
    </span>
  );
}
